import {makeAutoObservable, runInAction} from "mobx";
import {IShop} from "../../../../models/User";
import {rootContainer, TYPE} from "../../../../services/container/root";
import {IChangeable} from "../../../../stores/types";
import {getNullableChangesOrModel, isSet, toBoolean, toNullableNumber} from "../../../../models/model-types-methods";
import AdminManageMerchantsPlansService from "../../../../services/api/admin-manage-merchants-plans-service";
import {IPlanEditorModel, PLAN_CALCULATION_MECHANISMS} from "../../../../models/Plan";


interface IPlanBillingFieldsEditorChanges extends Partial<IPlanEditorModel> {}

class ManageMerchantsPlansPageStore implements IChangeable<IPlanEditorModel> {

    private _selectedMerchant?: IShop;
    private _model?: IPlanEditorModel;
    private _changes: IPlanBillingFieldsEditorChanges = {};
    private _isSaving: boolean = false;

    private get adminManageMerchantsPlansService(): AdminManageMerchantsPlansService {
        return rootContainer.get<AdminManageMerchantsPlansService>(TYPE.AdminManageMerchantsPlansService);
    }

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    public hasChanges() {
        return !!Object.keys(this._changes).length;
    }

    public getSelectedMerchant(): IShop | undefined {
        return this._selectedMerchant;
    }

    public getSelectedMerchantPlanModel(): IPlanEditorModel | undefined {
        return this._model;
    }

    public isNotAllRgoMarginsSet(): boolean {
        if (!this.getShipByReturnGoLabelCostRelativeMargin() && !this.getShipByReturnGoLabelCostFixedMargin()) {
            return false;
        }

        if (this.getShipByReturnGoLabelCostRelativeMargin() && this.getShipByReturnGoLabelCostFixedMargin()) {
            return false;
        }

        return !!this.getShipByReturnGoLabelCostFixedMargin() || !!this.getShipByReturnGoLabelCostRelativeMargin();
    }

    public isNotAllCoverageMarginsSet(): boolean {
        if (!this.getCoverageLabelCostRelativeMargin() && !this.getCoverageLabelCostFixedMargin() && !this.getCoverageRevenueShare()) {
            return false;
        }

        if (this.getCoverageLabelCostRelativeMargin() && this.getCoverageLabelCostFixedMargin() && this.getCoverageRevenueShare()) {
            return false;
        }

        return !!this.getCoverageLabelCostFixedMargin() || !!this.getCoverageLabelCostRelativeMargin() || !!this.getCoverageRevenueShare();
    }

    public getIsUsageCalculationMechanismIsFake(): boolean | undefined {
        return this._changes.usages_calculation_mechanism_is_fake ?? this._model?.usages_calculation_mechanism_is_fake;
    }

    public getCoverageLabelCostFixedMargin(): number | null | undefined {
        return getNullableChangesOrModel(this._changes.coverage_label_cost_fixed_margin, this._model?.coverage_label_cost_fixed_margin);
    }

    public getCoverageLabelCostRelativeMargin(): number | null | undefined {
        return getNullableChangesOrModel(this._changes.coverage_label_cost_relative_margin, this._model?.coverage_label_cost_relative_margin);
    }

    public getCoverageRevenueShare(): number | null | undefined {
        return getNullableChangesOrModel(this._changes.coverage_revenue_share, this._model?.coverage_revenue_share);
    }

    public getShipByReturnGoLabelCostFixedMargin(): number | null | undefined {
        return getNullableChangesOrModel(this._changes.ship_by_rgo_label_cost_fixed_margin, this._model?.ship_by_rgo_label_cost_fixed_margin);
    }

    public getShipByReturnGoLabelCostRelativeMargin(): number | null | undefined {
        return getNullableChangesOrModel(this._changes.ship_by_rgo_label_cost_relative_margin, this._model?.ship_by_rgo_label_cost_relative_margin);
    }

    public setIsUsageCalculationMechanismIsFake(v: boolean): void {
        this._changes.usages_calculation_mechanism_is_fake = v;
    }

    public setCoverageByReturnGoLabelCostRelativeMargin(v: number | null): void {
        this._changes.coverage_label_cost_relative_margin = v;
    }

    public setCoverageByReturnGoLabelCostFixedMargin(v: number | null): void {
        this._changes.coverage_label_cost_fixed_margin = v;
    }

    public setShipByReturnGoLabelCostFixedMargin(v: number | null): void {
        this._changes.ship_by_rgo_label_cost_fixed_margin = v;
    }

    public setCoverageRevenueShare(v: number | null): void {
        this._changes.coverage_revenue_share = v;
    }

    public setShipByReturnGoLabelCostRelativeMargin(v: number | null): void {
        this._changes.ship_by_rgo_label_cost_relative_margin = v;
    }

    public setSelectedMerchant(list: IShop[]): void {
        if (list.length === 0) {
            this._selectedMerchant = undefined;
            this._model = undefined;
            this._changes = {};
            return;
        }

        this._selectedMerchant = list[0];

        if(!isSet(this._selectedMerchant.plan_id)) {
            return;
        }

        this.fetchSelectedMerchantPlan();
    }

    public isNotRelevantUsagesCalculationMechanism(): boolean {
        const calculationMechanism = this._changes.usages_calculation_mechanism ?? this._model?.usages_calculation_mechanism;
        return calculationMechanism !== PLAN_CALCULATION_MECHANISMS.TRANSACTIONAL && calculationMechanism !== PLAN_CALCULATION_MECHANISMS.MIXED;
    }

    private async fetchSelectedMerchantPlan(): Promise<void> {
        const model = await this.adminManageMerchantsPlansService.fetchPlanBillingFields(this._selectedMerchant?.plan_id as number);

        runInAction(() => {
            this._model = ensureValidModelCopy(model);
        });
    }

    public getUpdatedModel(): IPlanEditorModel {
        return {
            ...this._model,
            ...this._changes,
        } as IPlanEditorModel;
    }

    public discardChanges(): void {
        this._changes = {};
    }

    async save(): Promise<void> {
        const data = this.getUpdatedModel();
        let model: IPlanEditorModel | undefined;
        this._isSaving = true;

        try {
            model = await this.adminManageMerchantsPlansService.updateUserPlanBillingFields(this._selectedMerchant?.id as number, data);
        }
        catch (e) {}

        runInAction(() => {
            if (model) {
                this._model = ensureValidModelCopy(model!);
                this.discardChanges();
            }
            this._isSaving = false;
        });

    }

    isSaving(): boolean {
        return this._isSaving;
    }

}

function ensureValidModelCopy(data: IPlanEditorModel): IPlanEditorModel {
    return {
        ...data,
        usages_calculation_mechanism_is_fake : toBoolean(data.usages_calculation_mechanism_is_fake),
        coverage_revenue_share: toNullableNumber(data.coverage_revenue_share),
        coverage_label_cost_fixed_margin: toNullableNumber(data.coverage_label_cost_fixed_margin),
        coverage_label_cost_relative_margin: toNullableNumber(data.coverage_label_cost_relative_margin),
        ship_by_rgo_label_cost_fixed_margin: toNullableNumber(data.ship_by_rgo_label_cost_fixed_margin),
        ship_by_rgo_label_cost_relative_margin: toNullableNumber(data.ship_by_rgo_label_cost_relative_margin)
    }
}

export default new ManageMerchantsPlansPageStore();